import { useCallback, useEffect, useRef, useState } from 'react';
import { InputTableCellParams, InputTableRowParams } from '../types';

export interface TableSelectedCell {
  id: string;
  field: string;
}

export type PasteResolver = (codes: string[]) => Promise<any[]>;
export type PasteConfigMap = Record<
  string,
  {
    resolver?: PasteResolver;
    resultField?: string;
  }
>;

export type TableRowBase = { uuid?: string | number; line?: number; [key: string]: any };

export interface UseTableRowsParams<RowType extends TableRowBase> {
  initialRows?: RowType[];
  defaultRows?: number;
  onChange?: (rows: RowType[]) => void;
  onFieldValueChange?: (row: RowType, field: string, value: any) => Partial<RowType>;
  defaultField?: string;
}

const normalizeRows = <RowType extends TableRowBase>(rowsInput: RowType[]): RowType[] =>
  (rowsInput || []).map((row, index) => ({
    ...(row || ({} as RowType)),
    uuid: row?.uuid !== undefined && row?.uuid !== null ? row.uuid.toString() : `temp-${Date.now()}-${index}`
  }));

const buildDefaultRows = <RowType extends TableRowBase>(count: number): RowType[] =>
  Array.from({ length: count }, (_, index) => ({
    uuid: `temp-${Date.now()}-${index}`,
    line: index + 1
  })) as RowType[];

const areRowsShallowEqual = <RowType extends TableRowBase>(a: RowType[], b: RowType[]) =>
  a.length === b.length &&
  a.every((row, idx) => {
    const other = b[idx];
    return row?.uuid?.toString() === other?.uuid?.toString() && row?.line === other?.line;
  });

const hasRowFieldChanges = <RowType extends TableRowBase>(row: RowType, fields: Partial<RowType>) =>
  Object.entries(fields).some(([field, value]) => !Object.is(row[field], value));

export function useTableRows<RowType extends TableRowBase>({
  initialRows = [],
  defaultRows = 0,
  onChange,
  onFieldValueChange,
  defaultField = 'dien_giai'
}: UseTableRowsParams<RowType>) {
  const computeInitial = () => {
    const normalized = normalizeRows(initialRows);
    if (normalized.length) return normalized;
    if (defaultRows > 0) return buildDefaultRows<RowType>(defaultRows);
    return [];
  };

  const [rows, setRows] = useState<RowType[]>(computeInitial);
  const [selectedRowUuid, setSelectedRowUuid] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<RowType | null>(null);
  const [selectedCell, setSelectedCell] = useState<TableSelectedCell | null>(null);
  const [isPasting, setIsPasting] = useState<boolean>(false);

  const nonWritableFieldsRef = useRef<Set<string>>(new Set<string>());
  const pasteConfigRef = useRef<PasteConfigMap>({});
  const rowsRef = useRef<RowType[]>(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    const normalized = normalizeRows(initialRows);
    let nextRows = normalized;

    if (!normalized.length) {
      nextRows = rowsRef.current.length
        ? rowsRef.current
        : defaultRows > 0
          ? buildDefaultRows<RowType>(defaultRows)
          : [];
    }

    if (areRowsShallowEqual(rowsRef.current, nextRows)) return;
    setRows(nextRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRows.length, defaultRows]);

  const handleRowClick = useCallback((params: InputTableRowParams<RowType>) => {
    const rowUuid = params.id?.toString() || params.row?.uuid?.toString();
    if (!rowUuid) return;

    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row as RowType);
  }, []);

  const handleCellClick = useCallback((params: InputTableCellParams<RowType>) => {
    const rowUuid = params.id.toString();
    if (!rowUuid) return;

    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row as RowType);

    setSelectedCell({
      id: rowUuid,
      field: params.field
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRowUuid(null);
    setSelectedRow(null);
    setSelectedCell(null);
  }, []);

  const handleCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: RowType[keyof RowType]) => {
      setRows(previousRows => {
        const rowIndex = previousRows.findIndex(row => row.uuid === rowUuid);
        if (rowIndex === -1) {
          return previousRows;
        }

        const currentRow = previousRows[rowIndex];
        const extraFields = onFieldValueChange?.(currentRow, field, newValue) ?? {};

        const updatedRow = {
          ...currentRow,
          [field]: newValue,
          ...extraFields
        };

        if (!hasRowFieldChanges(currentRow, updatedRow)) {
          return previousRows;
        }

        const updatedRows = [...previousRows];
        updatedRows[rowIndex] = updatedRow;

        onChange?.(updatedRows);

        if (rowUuid === selectedRowUuid) {
          setSelectedRow(updatedRow);
        }

        return updatedRows;
      });
    },
    [onChange, onFieldValueChange, selectedRowUuid]
  );

  const handleAddRow = useCallback(() => {
    const hasLineField = rows.length > 0 && rows[0].line !== undefined;
    const newRow: any = {
      uuid: `temp-${Date.now()}`,
      ...(hasLineField ? { line: rows.length + 1 } : {})
    };

    const newRows = [...rows, newRow];
    setRows(newRows);
    onChange?.(newRows);

    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  }, [onChange, rows]);

  const handleDeleteRow = useCallback(() => {
    if (!selectedRowUuid) return;

    const updatedRows = rows.filter((row: RowType) => row.uuid !== selectedRowUuid);

    const reorderedRows = updatedRows.map((row: RowType, index: number) => ({
      ...row,
      ...(row.line !== undefined && { line: index + 1 })
    }));

    setRows(reorderedRows);
    onChange?.(reorderedRows);

    if (reorderedRows.length > 0) {
      const lastRow = reorderedRows[reorderedRows.length - 1];
      setSelectedRowUuid(lastRow.uuid?.toString() ?? null);
      setSelectedRow(lastRow);
    } else {
      clearSelection();
    }
  }, [clearSelection, onChange, rows, selectedRowUuid]);

  const handleCopyRow = useCallback(() => {
    if (!selectedRow) return;

    const { uuid, ...rowDataWithoutUuid } = selectedRow;
    const hasLineField = rows.length > 0 && rows[0].line !== undefined;

    const copiedRow: RowType = {
      ...(rowDataWithoutUuid as RowType),
      uuid: `temp-${Date.now()}`,
      ...(hasLineField ? { line: rows.length + 1 } : {})
    };

    const newRows = [...rows, copiedRow];
    setRows(newRows);
    onChange?.(newRows);

    setSelectedRowUuid(copiedRow.uuid?.toString() ?? null);
    setSelectedRow(copiedRow);
  }, [onChange, rows, selectedRow]);

  const handleMoveRow = useCallback(
    (direction: 'up' | 'down') => {
      if (!selectedRowUuid) return;

      const currentIndex = rows.findIndex((row: RowType) => row.uuid === selectedRowUuid);
      if (currentIndex === -1) return;

      const newIndex = direction === 'up' ? Math.max(0, currentIndex - 1) : Math.min(rows.length - 1, currentIndex + 1);

      if (newIndex === currentIndex) return;

      const newRows = [...rows];
      const [movedRow] = newRows.splice(currentIndex, 1);
      newRows.splice(newIndex, 0, movedRow);

      newRows.forEach((row: RowType, index: number) => {
        if (row.line !== undefined) {
          (row as any).line = index + 1;
        }
      });

      setRows(newRows);
      onChange?.(newRows);

      setSelectedRow(movedRow);
    },
    [onChange, rows, selectedRowUuid]
  );

  const setNonWritableFields = useCallback((fields: Set<string>) => {
    nonWritableFieldsRef.current = fields;
  }, []);

  const setPasteConfigs = useCallback((configs: PasteConfigMap) => {
    pasteConfigRef.current = configs;
  }, []);

  const handlePasteRow = useCallback(
    async (clipboardText?: string) => {
      const isRowEmpty = (row: RowType): boolean =>
        Object.entries(row).every(
          ([key, value]) => ['uuid', 'line'].includes(key) || value === undefined || value === null || value === ''
        );
      const isWritableField = (field: string) => !nonWritableFieldsRef.current.has(field);
      const updateRowsAndSelection = (newRows: RowType[], targetRow: RowType, targetField?: string) => {
        setRows(newRows);
        onChange?.(newRows);
        setSelectedRowUuid(targetRow.uuid as string);
        setSelectedRow(targetRow);
        if (targetField) {
          setSelectedCell({ id: targetRow.uuid as string, field: targetField });
        }
      };

      const clipboardProvided = typeof clipboardText === 'string';

      if (clipboardProvided) {
        setIsPasting(true);
        try {
          const lines = (clipboardText || '')
            .split(/\r?\n/)
            .map(l => l.trim())
            .filter(Boolean);
          if (lines.length === 0) {
            setIsPasting(false);
            return;
          }

          const hasLineField = rows.some(row => row.line !== undefined);
          const targetField = selectedCell?.field || defaultField;
          if (!isWritableField(targetField)) {
            setIsPasting(false);
            return;
          }

          const pasteConfig = pasteConfigRef.current[targetField];
          const resolvedValues = pasteConfig?.resolver ? (await pasteConfig.resolver(lines)) || lines : lines;

          const selectedUuid = selectedCell?.id || selectedRowUuid;
          const selectedIndex = selectedUuid ? rows.findIndex(row => row.uuid === selectedUuid) : -1;
          const insertPosition = selectedIndex >= 0 ? selectedIndex : 0;

          const baseRows = rows.length === 1 && isRowEmpty(rows[0]) ? [] : [...rows];
          const fieldName = pasteConfig?.resultField || targetField;
          const baseTimestamp = Date.now();

          const rowsWithNew: RowType[] = [];
          for (let i = 0; i < insertPosition && i < baseRows.length; i++) {
            rowsWithNew.push(baseRows[i]);
          }

          const template = baseRows[baseRows.length - 1] || ({} as RowType);
          const { uuid: templateUuid, line: templateLine, ...templateData } = template;

          for (let idx = 0; idx < resolvedValues.length; idx++) {
            const targetIndex = insertPosition + idx;
            const value = resolvedValues[idx];

            if (targetIndex < baseRows.length) {
              const baseRow = baseRows[targetIndex];
              const extra = onFieldValueChange?.(baseRow, fieldName, value) ?? {};
              rowsWithNew.push({
                ...baseRow,
                [fieldName]: value,
                ...extra
              } as RowType);
            } else {
              const templateRow: RowType = {
                ...templateData,
                uuid: `temp-${baseTimestamp}-${idx}`
              } as RowType;
              const extra = onFieldValueChange?.(templateRow, fieldName, value) ?? {};
              rowsWithNew.push({
                ...templateRow,
                [fieldName]: value,
                ...extra
              } as RowType);
            }
          }

          const remainingStart = insertPosition + resolvedValues.length;
          for (let i = remainingStart; i < baseRows.length; i++) {
            rowsWithNew.push(baseRows[i]);
          }

          const normalizedRows = hasLineField
            ? rowsWithNew.map((row, idx) => ({ ...row, line: idx + 1 }))
            : rowsWithNew;

          const lastAffected =
            normalizedRows[Math.min(insertPosition + resolvedValues.length - 1, normalizedRows.length - 1)];
          updateRowsAndSelection(normalizedRows, lastAffected, targetField);
        } finally {
          setIsPasting(false);
        }
        return;
      }

      if (!selectedRow) return;

      const { uuid, line, ...rowData } = selectedRow;
      const newRow: RowType = {
        ...rowData,
        uuid: `temp-${Date.now()}`,
        ...(line !== undefined && { line: rows.length + 1 })
      } as RowType;

      const selectedIndex = selectedRowUuid ? rows.findIndex(row => row.uuid === selectedRowUuid) : -1;
      const newRows =
        selectedIndex >= 0
          ? (() => {
              const result = [...rows];
              result.splice(selectedIndex + 1, 0, newRow);
              return result;
            })()
          : [...rows, newRow];

      updateRowsAndSelection(newRows, newRow);
    },
    [defaultField, onChange, onFieldValueChange, rows, selectedCell, selectedRow, selectedRowUuid]
  );

  return {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handleMoveRow,
    handlePasteRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  };
}
